// How the live map SHOWS a staff member's status. The status itself is Bam's
// per-role sheet, computed once in packages/logic (`staff-ops-status.ts`) and
// delivered on the staff-location snapshot as `opsStatus`. This file only turns
// it into a colour, a group for the panel and filters, and a fallback for an
// Edge Function deployed before `opsStatus` existed.

import type { StaffOpsActivityValue } from "@bednbody/types";
import type { Tone } from "./LiveMapCards";
import {
  cssVar,
  therapistMapStatusLabel,
  type ClockedInStaffMapItem,
  type TherapistMapStatus,
} from "./live-map.shared";

/** The panel's staff groups, in the order an operator should read them. */
export const STAFF_GROUPS = [
  "disconnected",
  "on_the_road",
  "at_client",
  "waiting_for_pick_up",
  "on_break",
  "active",
  "not_in_session",
] as const;
export type StaffGroup = (typeof STAFF_GROUPS)[number];

export const STAFF_GROUP_LABELS: Record<StaffGroup, string> = {
  disconnected: "Disconnected",
  on_the_road: "On the road",
  at_client: "At the client",
  waiting_for_pick_up: "Waiting for pick-up",
  on_break: "On break",
  active: "Active",
  not_in_session: "Not in session",
};

const GROUP_OF_ACTIVITY: Record<StaffOpsActivityValue, StaffGroup> = {
  pick_up_en_route: "on_the_road",
  drop_off_en_route: "on_the_road",
  return_en_route: "on_the_road",
  enroute: "on_the_road",
  at_client_waiting_to_start: "at_client",
  on_going_service: "at_client",
  collecting_payment: "at_client",
  waiting_for_pick_up: "waiting_for_pick_up",
  on_break: "on_break",
  active: "active",
  not_in_session: "not_in_session",
};

/** Before `opsStatus` shipped: TEND-024's six, grouped as closely as they go. */
const GROUP_OF_MAP_STATUS: Record<TherapistMapStatus, StaffGroup> = {
  offline: "disconnected",
  service: "at_client",
  dispatch: "on_the_road",
  pause: "on_break",
  active: "active",
  off_session: "not_in_session",
};

export interface StaffStatusView {
  /** What they are doing underneath any Disconnected label; `null` on the old fallback. */
  activity: StaffOpsActivityValue | null;
  label: string;
  group: StaffGroup;
  /** A theme token value, for the map pin's status dot and the row chip. */
  color: string;
  since: string | null;
  bookingId: string | null;
  bookingRef: string | null;
  /** True when this came from the engine; false for the old six-status fallback. */
  fromEngine: boolean;
}

/**
 * Each group's one tone — the pill, the avatar dot, the count tile and the map
 * pin all wear it, so a colour means the same thing everywhere on the screen.
 */
export const GROUP_TONE: Record<StaffGroup, Tone> = {
  disconnected: "danger",
  on_the_road: "info",
  at_client: "success",
  waiting_for_pick_up: "warning",
  on_break: "gold",
  active: "primary",
  not_in_session: "muted",
};

export function groupColor(group: StaffGroup): string {
  switch (group) {
    case "disconnected":
      return cssVar("--destructive", "#d15757");
    case "on_the_road":
      return cssVar("--info", "#4599b5");
    case "at_client":
      return cssVar("--success", "#45a16b");
    case "waiting_for_pick_up":
      return cssVar("--warning", "#e09729");
    case "on_break":
      return cssVar("--gold", "#d6a93a");
    case "active":
      return cssVar("--primary", "#009179");
    case "not_in_session":
      return cssVar("--muted-foreground", "#6a6c6a");
  }
}

export function staffStatusView(
  staff: ClockedInStaffMapItem,
  riderIsDispatched: boolean,
): StaffStatusView {
  const ops = staff.opsStatus;
  if (ops) {
    // Disconnected is a modifier on the activity (the sheet's whole point), so
    // it outranks the activity's own group — but the LABEL keeps the activity.
    const group =
      !ops.connected && ops.activity !== "not_in_session"
        ? "disconnected"
        : GROUP_OF_ACTIVITY[ops.activity];
    return {
      activity: ops.activity,
      label: ops.label,
      group,
      color: groupColor(group),
      since: ops.since,
      bookingId: ops.bookingId,
      bookingRef: ops.bookingRef,
      fromEngine: true,
    };
  }
  const group: StaffGroup =
    staff.role === "therapist"
      ? staff.mapStatus
        ? GROUP_OF_MAP_STATUS[staff.mapStatus]
        : "not_in_session"
      : riderIsDispatched
        ? "on_the_road"
        : staff.locationState === "live"
          ? "active"
          : "not_in_session";
  return {
    activity: null,
    label:
      therapistMapStatusLabel(staff.mapStatus) ??
      (staff.role === "rider" ? STAFF_GROUP_LABELS[group] : "Status unavailable"),
    group,
    color: groupColor(group),
    since: null,
    bookingId: null,
    bookingRef: null,
    fromEngine: false,
  };
}

/** "since 2:14 PM" — the sheet's "every status carries its time". */
export function sinceLabel(iso: string | null): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return `since ${at.toLocaleTimeString("en-PH", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Manila",
  })}`;
}
